/**
 * Presentation helpers for the clean citation artifact.
 *
 * These bridge the typed contract (`types/citationArtifact.ts`) onto the
 * existing `CitedExcelCell` renderer so verified Excel cells keep their click
 * behaviour. No relinking, no display recomputation — `display` is shown as-is.
 */

import type { CitedCellValue, ExcelCellCitation } from "../types/citations";
import type {
	CitedTableArtifact,
	DerivedSourceRef,
	ExtractionCell,
	SourceRef,
	VerificationStatus,
} from "../types/citationArtifact";
import { safeParseCitedTableArtifact } from "../types/citationArtifact";

/** What a cited-table block should render as, given what this client supports. */
export type CitedTableRender =
	| { kind: "typed"; artifact: CitedTableArtifact }
	| { kind: "fallback"; markdown: string }
	| { kind: "none" };

/**
 * Decide how to render a `cited_table_artifact` block.
 *
 * Keyed-only delivery strips the table out of the message text, so the block is
 * the only copy of the data. Whenever the typed view is unavailable — the
 * feature flag is off, or the artifact fails schema validation — we fall back to
 * the backend's markdown projection instead of rendering nothing. `none` is
 * reached only when the backend sent no projection either (a `legacy`-mode
 * block, where the message text still carries the table).
 */
export function resolveCitedTableRender(
	block: { artifact: unknown; fallback_markdown?: string | null },
	artifactsEnabled: boolean,
): CitedTableRender {
	if (artifactsEnabled) {
		const parsed = safeParseCitedTableArtifact(block.artifact);
		if (parsed.success && parsed.data) {
			return { kind: "typed", artifact: parsed.data };
		}
	}
	const markdown = block.fallback_markdown;
	if (typeof markdown === "string" && markdown.trim()) {
		return { kind: "fallback", markdown };
	}
	return { kind: "none" };
}

/** One row of a derived value's input trace: a component leaf with its sign. */
export interface DerivedInputTrace {
	sign: 1 | -1;
	label?: string; // the component's metric/row label
	display?: string; // the component's displayed value
	status?: VerificationStatus; // the component leaf's own verification
	citation?: ExcelCellCitation; // clickable source cell, when resolvable
}

export type CellPresentation =
	| { kind: "missing"; placeholder: string }
	// clickable verified Excel cell.
	| { kind: "excel"; cell: CitedCellValue }
	| {
			kind: "plain";
			display: string;
			unverified: boolean;
			issue?: string;
	  }
	| {
			// Derived aggregate (e.g. a sum of explicit component cells).
			kind: "derived";
			display: string;
			verified: boolean;
			firstInput?: CitedCellValue; // click target: the first component cell
			components: string; // "Sheet!A1, Sheet!A3, ..."
			expression?: string;
			// Whether the expression is a plain sum/difference of its inputs. When
			// it is not (a ratio, a product), the per-input `sign` below cannot
			// describe it and the renderer must show the expression itself rather
			// than a reconstructed "a + b".
			additive: boolean;
			issue?: string;
			// Stage 4 traceability: each component resolved to its leaf (label,
			// value, verification, source cell) with the sign it enters the formula.
			inputs: DerivedInputTrace[];
	  };

export const MISSING_PLACEHOLDER = "—";

/**
 * Index every cell's `CitedValue` by its `value_id`, so a derived value can
 * resolve its `input_value_ids` to the real leaf nodes (label, value,
 * verification, source cell) for an input trace. O(cells), built once per view.
 */
export function buildValueIndex(
	artifact: CitedTableArtifact,
): Map<string, ExtractionCell> {
	const index = new Map<string, ExtractionCell>();
	for (const cell of Object.values(artifact.cells)) {
		const id = cell.value?.value_id;
		if (id) index.set(id, cell);
	}
	return index;
}

/**
 * Is this expression a plain sum/difference of its terms?
 *
 * Only such an expression can be described by a per-input sign. `gpr / egi` and
 * `units * rent` cannot: rendering either as "GPR + EGI" would state a
 * calculation that never happened, on a value carrying a verified badge. So the
 * question is asked explicitly rather than assumed, and the answer decides
 * whether the trace reconstructs a formula or shows the real one.
 */
export function isAdditiveExpression(expression: string | undefined): boolean {
	if (!expression) return true;
	return !/[*/]/.test(expression);
}

/** Ordered +/- signs of an `a0 - a1 + a2`-style expression (one per term). */
function expressionSigns(expression: string | undefined, count: number): (1 | -1)[] {
	if (!expression) return Array<1 | -1>(count).fill(1);
	const signs: (1 | -1)[] = [];
	const re = /([+-])?\s*[A-Za-z$][A-Za-z0-9$]*/g;
	let m: RegExpExecArray | null;
	while ((m = re.exec(expression)) !== null) {
		signs.push(m[1] === "-" ? -1 : 1);
	}
	return signs.length === count ? signs : Array<1 | -1>(count).fill(1);
}

function excelSourcesToCitations(sources: SourceRef[]): ExcelCellCitation[] {
	return sources
		.filter((s): s is Extract<SourceRef, { kind: "excel" }> => s.kind === "excel")
		.map((s) => ({
			kind: "excel",
			file_id: s.file_id,
			filename: s.filename ?? undefined,
			sheet: s.sheet,
			cell: s.cell ?? undefined,
			range: s.range ?? undefined,
		}));
}

/**
 * Resolve how a ledger cell should render.
 *
 * - verified + Excel source -> clickable `CitedExcelCell`
 * - verified without a clickable source -> plain display
 * - unverified / ambiguous / invalid -> plain display flagged unverified
 * - missing / not_applicable / not_present / no value -> placeholder
 */
export function presentCell(
	cell: ExtractionCell | undefined,
	placeholder: string = MISSING_PLACEHOLDER,
	valueIndex?: Map<string, ExtractionCell>,
): CellPresentation {
	// Only a 'filled' cell can render a value/link. Trust cell.status first so a
	// non-filled cell can never render a leftover value as a live link.
	if (!cell || cell.status !== "filled" || !cell.value) {
		return { kind: "missing", placeholder };
	}

	const { value } = cell;
	const status = value.verification.status;

	if (status === "missing") {
		return { kind: "missing", placeholder };
	}

	// Derived aggregate — render distinctly (badge + component list), never as a
	// plain single-cell citation.
	const derived = value.sources.find(
		(s): s is DerivedSourceRef => s.kind === "derived",
	);
	if (derived) {
		// Keep the raw Excel inputs alongside their citations — they carry the
		// hydrated row label + value that make an input legible as "<metric> =
		// <value>" instead of a bare "Sheet!B63".
		const excelInputs = derived.inputs.filter(
			(s): s is Extract<SourceRef, { kind: "excel" }> => s.kind === "excel",
		);
		const sourceCitations = excelSourcesToCitations(derived.inputs);
		const first = sourceCitations[0];
		const components = sourceCitations
			.map((i) => `${i.sheet}!${i.cell ?? i.range ?? ""}`)
			.join(", ");

		// Input trace: prefer the graph edges (input_value_ids -> leaf nodes,
		// which carry label/value/verification); fall back to the derived source
		// cells (with their hydrated label/value) when a leaf isn't in this
		// artifact (older nodes / cross-file).
		const ids = value.input_value_ids ?? [];
		const traceLen = ids.length || derived.inputs.length;
		const expression = derived.expression ?? undefined;
		const additive = isAdditiveExpression(expression);
		// A non-additive expression has no per-term sign to read; the signs below
		// stay at their +1 default and the renderer ignores them.
		const signs = additive
			? expressionSigns(expression, traceLen)
			: Array<1 | -1>(traceLen).fill(1);
		const inputs: DerivedInputTrace[] =
			ids.length > 0
				? ids.map((id, i) => {
						const leaf = valueIndex?.get(id)?.value;
						const input = excelInputs[i];
						const citation = leaf
							? excelSourcesToCitations(leaf.sources)[0]
							: sourceCitations[i];
						return {
							sign: signs[i] ?? 1,
							label: leaf?.metric_label ?? input?.label ?? undefined,
							display: leaf?.display ?? input?.display_value ?? undefined,
							status: leaf?.verification.status,
							citation,
						};
				  })
				: excelInputs.map((input, i) => ({
						sign: signs[i] ?? 1,
						label: input.label ?? undefined,
						display: input.display_value ?? undefined,
						citation: sourceCitations[i],
				  }));

		return {
			kind: "derived",
			display: value.display,
			verified: status === "verified",
			firstInput: first
				? { value: value.value, display_value: value.display, citations: [first] }
				: undefined,
			components,
			expression,
			additive,
			issue: value.verification.issues?.[0]?.message,
			inputs,
		};
	}

	if (status === "verified") {
		const citations = excelSourcesToCitations(value.sources);
		if (citations.length > 0) {
			return {
				kind: "excel",
				cell: {
					value: value.value,
					display_value: value.display,
					citations,
				},
			};
		}
		return {
			kind: "plain",
			display: value.display,
			unverified: false,
		};
	}

	// unverified / ambiguous / invalid: show the value, never a verified link.
	// Surface the first verification issue so the reason is legible in the UI.
	const issue = value.verification.issues?.[0]?.message;
	return { kind: "plain", display: value.display, unverified: true, issue };
}

/** Resolve a row+column to its ledger cell via the slot indirection. */
export function resolveCell(
	artifact: CitedTableArtifact,
	rowCells: Record<string, string>,
	columnKey: string,
): ExtractionCell | undefined {
	const slotId = rowCells[columnKey];
	return slotId ? artifact.cells[slotId] : undefined;
}
